import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { X, Send, Loader2, Bot, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// FAQ responses for common questions (fallback if API fails)
const FAQ_RESPONSES = {
  'fees': 'RouteFlow AI charges a transparent 0.48% service fee plus a small network fee (typically $2-4). You\'ll see the exact total before confirming any transfer.',
  'countries': 'We currently support 121 countries worldwide including US, EU, UK, Mexico, Philippines, Nigeria, and many more. Check our Supported Countries page for the full list.',
  'time': 'Most transfers complete in 15-45 minutes depending on the route. Our AI finds the fastest path for your specific transfer.',
  'safe': 'Yes! We use bank-level 256-bit encryption, non-custodial wallets (you control your funds), and blockchain technology for secure, transparent transfers.',
  'minimum': 'Minimum transfer is $10 USD. Maximum is $10,000 USD per transaction. Higher limits are available for verified business accounts.',
  'cancel': 'Once a transfer is processing on the blockchain, it cannot be canceled. Please contact support immediately if there\'s an issue.',
  'currencies': 'We support 44 fiat currencies including USD, EUR, GBP, CAD, AED, INR, NGN, and many more.',
  'default': 'I\'m here to help! You can ask me about fees, supported countries, transfer times, security, or how RouteFlow AI works. For complex issues, please email support@routeflowai.com'
};

function getKeywordResponse(message) {
  const lowerMsg = message.toLowerCase();
  if (lowerMsg.includes('fee') || lowerMsg.includes('cost') || lowerMsg.includes('price') || lowerMsg.includes('charge')) {
    return FAQ_RESPONSES.fees;
  }
  if (lowerMsg.includes('country') || lowerMsg.includes('countries') || lowerMsg.includes('where')) {
    return FAQ_RESPONSES.countries;
  }
  if (lowerMsg.includes('time') || lowerMsg.includes('long') || lowerMsg.includes('fast') || lowerMsg.includes('speed')) {
    return FAQ_RESPONSES.time;
  }
  if (lowerMsg.includes('safe') || lowerMsg.includes('secure') || lowerMsg.includes('security') || lowerMsg.includes('trust')) {
    return FAQ_RESPONSES.safe;
  }
  if (lowerMsg.includes('minimum') || lowerMsg.includes('maximum') || lowerMsg.includes('limit')) {
    return FAQ_RESPONSES.minimum;
  }
  if (lowerMsg.includes('cancel') || lowerMsg.includes('refund') || lowerMsg.includes('stop')) {
    return FAQ_RESPONSES.cancel;
  }
  if (lowerMsg.includes('currency') || lowerMsg.includes('currencies') || lowerMsg.includes('fiat')) {
    return FAQ_RESPONSES.currencies;
  }
  return null;
}

export default function AIChatWidget({ isOpen, onClose }) {
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: 'Hi! 👋 I\'m RouteFlow AI Assistant. How can I help you today? You can ask me about fees, supported countries, transfer times, or how our service works.' 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // First try the backend AI
      const response = await fetch(`${BACKEND_URL}/api/voice/text-command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: userMessage,
          user_id: 'guest',
          language: 'en'
        })
      });

      const data = await response.json();
      
      if (data.message || data.response_text) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.message || data.response_text 
        }]);
      } else {
        // Fallback to keyword matching
        const keywordResponse = getKeywordResponse(userMessage);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: keywordResponse || FAQ_RESPONSES.default 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      // Fallback to keyword matching on error
      const keywordResponse = getKeywordResponse(userMessage);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: keywordResponse || FAQ_RESPONSES.default 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md h-[500px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold">RouteFlow AI Assistant</h3>
              <p className="text-xs text-emerald-100">Online • Ready to help</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' ? 'bg-emerald-600' : 'bg-teal-600'
                }`}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className={`rounded-2xl px-4 py-2 ${
                  msg.role === 'user' 
                    ? 'bg-emerald-600 text-white rounded-tr-sm' 
                    : 'bg-white text-gray-800 shadow-sm rounded-tl-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white rounded-2xl px-4 py-2 shadow-sm">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-white rounded-b-lg">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your question..."
              className="flex-1"
              disabled={isLoading}
            />
            <Button 
              onClick={handleSend} 
              disabled={!input.trim() || isLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Powered by RouteFlow AI
          </p>
        </div>
      </Card>
    </div>
  );
}
